/* eslint-disable max-lines */
import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewEncapsulation, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, ParamMap, NavigationEnd } from '@angular/router';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { combineLatest, iif, Observable, of, Subscription, zip } from 'rxjs';

import { HeaderFacade } from '@header/header.facade';
import { Hotel } from '@hotels/hotels-home/entities/hotel';
import { Destination } from '@hotels/hotels-home/entities/destination';
import { PopularPlace } from '@hotels/hotels-home/entities/popular-place';
import { HotelsHomeFacade } from '@hotels/hotels-home/hotels-home.facade';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { HomeDataResponse } from '@app/modules/shared/home-sections/entities/home-data.response';
import { HomeFacade } from '@home/home.facade';
import { SearchType } from '@search-bar/entities/search-type';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { isNullOrUndefined } from '@app/util/util';
import { AppFacade } from '@app/app.facade';
import { TravelerTypes } from '@reservation/pax/entities/traveler-info';
import { SessionStorageService } from '@app/services/session-storage.service';
import { PlaceNamePipe } from '@app/pipes/place-name.pipe';
import { PlaceSelectors } from '@app/entities/place';
import { Room } from '@shared/base-search-bar/entities/room.entities';
import { RappiTravelModules } from '@ux/entities/modules';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { blockSearchByDates, canSearch, compareToCurrentDateWithoutTime, formatDateByRegex } from '@ux/mappers/date.mapper';
import { dateHotelValidation, mapPayloadHotel } from '@hotels/hotel-results/mappers/hotel-results.mapper';
import { HotelSearchPayload } from '@hotels/hotel-results/entities/hotel-search.payload';
import { byhoursHomeRootRoute, hotelsHomeRootRoute } from '@hotels/hotels-home/hotels-home-routing.module';
import { HotelDestinationInputType, InputDestinationPlace } from '@shared/base-search-bar/entities/autocomplete-destionation';
import { productHomeRoute } from '@app/constants/app-routing.constants';

@Component({
  selector: 'app-hotels-home',
  templateUrl: './hotels-home.container.html',
  styleUrls: ['./hotels-home.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HotelsHomeContainer implements OnInit, OnDestroy {

  public loadings: number = 4;
  public borderColor: boolean = false;
  public searchType: SearchType = SearchType.HOME_HOTELS;
  public source: string = EVENT_SOURCE.TRAVEL_HOME;
  public showInput: boolean;
  public loadDefaultplaces: boolean;
  public isByHours = false;

  private calendarRoute: string = 'calendar';
  private subscriptions: Subscription = new Subscription();
  private params: HotelSearchPayload = {} as HotelSearchPayload;

  constructor(
    private readonly facade: HotelsHomeFacade,
    private readonly headerFacade: HeaderFacade,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly appFacade: AppFacade,
    private readonly baseSearchBarFacade: BaseSearchBarFacade,
    private readonly mobileDeviceService: MobileDeviceService,
    private readonly homeFacade: HomeFacade,
    private readonly domManipulateService: DomManipulateService,
    private readonly placeNamePipe: PlaceNamePipe,
    private readonly sessionStorageService: SessionStorageService,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) { }

  ngOnInit(): void {
    this.isByHours = this.isByhoursRoute(this.router.url);
    this.checkRoute();
    if (this.isBrowser) {
      this.loadDefaultplaces = true;
      this.baseSearchBarFacade.resetBranchCode();
      this.baseSearchBarFacade.resetSalesChannel();
      this.baseSearchBarFacade.resetPackageStep();
      this.facade.fetchPopularDestinations();
      this.facade.fetchPopularHotels();
      this.facade.fetchPopularPlaces();
      this.borderColor = true;
      this.subscriptions.add(
        this.isMobile$.subscribe(isMobile => {
          if (!isMobile) this.router.navigate(['/']);
          else this.domManipulateService.setBodyScroll(false);
        })
      );
      this.listenParams();
    }
  }

  ngOnDestroy(): void {
    this.domManipulateService.setBodyScroll(true);
    this.subscriptions.unsubscribe();
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get popularDestinations$(): Observable<Destination[]> {
    return this.facade.popularDestinations$;
  }

  get popularHotels$(): Observable<Hotel[]> {
    return this.facade.popularHotels$;
  }

  get popularPlaces$(): Observable<PopularPlace[]> {
    return this.facade.popularPlaces$;
  }

  get isLoadingPopularDestinations(): Observable<boolean> {
    return this.facade.isLoadingPopularDestinations$;
  }

  get isLoadingPopularHotels$(): Observable<boolean> {
    return this.facade.isLoadingPopularHotels$;
  }

  get isLoadingPopularPlaces$(): Observable<boolean> {
    return this.facade.isLoadingPopularPlaces$;
  }

  get homeData$(): Observable<HomeDataResponse> {
    return this.homeFacade.homeData$;
  }

  public isByhoursRoute(url: string): boolean {
    return url.includes(byhoursHomeRootRoute);
  }

  public checkRoute() {
    this.subscriptions.add(this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.isByHours = this.isByhoursRoute(event.url);
    }));
  }

  public showMobileCalendar(type: CalendarField): void {
    this.router.navigate(
      [{ outlets: { search: [this.calendarRoute] } }],
      {
        state: {
          activeType: type,
          departurePlaceholder: this.isByHours ? 'SEARCH_MODULE.LOCATION_SEARCH.ENTRY' : 'SEARCH_MODULE.LOCATION_SEARCH.DEPART',
          returnPlaceholder: 'SEARCH_MODULE.LOCATION_SEARCH.RETURN',
          searchType: this.isByHours ? SearchType.BYHOURS_RESULTS : SearchType.HOTELS_RESULTS
        },
        relativeTo: this.route, skipLocationChange: true
      });
  }

  public showDestination(value: boolean): void {
    this.showInput = value;
  }

  public showRoomsEdition(): void {
    this.showInput = true;
  }

  private listenParams(): void {
    this.subscriptions.add(
      combineLatest([this.route.paramMap, this.route.queryParamMap?.pipe(take(1))])
        .pipe(
          switchMap(([params, queries]) => this.isMobile$.pipe(
            take(1),
            map(isMobile => ({ params, queries, isMobile }))
          )),
          filter(({ params }) => this.filterParams(params)),
          tap(({ params, queries, isMobile }) => {
            this.loadDefaultplaces = false;
            this.params = mapPayloadHotel(params['params'], isMobile, this.route.snapshot.queryParams.from);
            this.setGuestsByParams();
            const branchCode = queries.get('branch_code');
            const salesChannel = queries.get('sales_channel');
            if (branchCode) {
              this.baseSearchBarFacade.setBranchCode(branchCode);
            }
            if (salesChannel) {
              this.baseSearchBarFacade.setSalesChannel(salesChannel);
            }
            this.baseSearchBarFacade.searchDestinationPlaceInfo({
              id: params.get('destination_id'),
              type: params?.get('destination_type') as HotelDestinationInputType
            });
          }),
          switchMap(({ params }) => this.baseSearchBarFacade.hotelDestinationPlace$.pipe(
            filter(hotelDestinationPlace => !!hotelDestinationPlace.Id &&
              !!hotelDestinationPlace.InputType &&
              !!hotelDestinationPlace.NearestIata &&
              params.get('destination_id') === hotelDestinationPlace.Id.toString()),
            take(1),
            tap(destination => {
              this.sessionStorageService.setEncodeDestinationType(
                destination.InputType);
              this.sessionStorageService.setEncodeDestinationId(
                destination.Id);
              this.sessionStorageService.setEncodeDestinationIATA(
                destination.NearestIata);
            }),
            switchMap(input => {
              return iif(
                () => input.InputType === HotelDestinationInputType.hotel ?
                  !input.address : !input.NameFull,
                this.placeNamePipe.transform(
                  input.NearestIata, PlaceSelectors.Name), of(null)
              ).pipe(
                map(placeName => this.destination(params, placeName, input)),
                switchMap(({ destination: destinationPlace }) => zip(
                  this.route.queryParamMap.pipe(take(1)),
                  this.placeNamePipe.transform(destinationPlace.NearestIata, PlaceSelectors.Country),
                  this.placeNamePipe.transform(destinationPlace.NearestIata, PlaceSelectors.City)
                ).pipe(
                  take(1),
                  map(([queries, country, city]) => 
                    this.getDestinationFormat(destinationPlace, params, queries, country, city)),
                  map(({ queries, country, city, destinationFormat }) => {
                    this.baseSearchBarFacade.selectHotelDestinationPlace(
                      destinationFormat);
                    return { queries, country, city, dest: destinationFormat, params };
                  })
                ))
              );
            })
          )),
          tap(({ dest, country, city, params }) => {
            if (canSearch(RappiTravelModules.hotels)) {
              this.payloadSearchData(params);
            } else {
              const destinationPlace = {
                code: dest.NearestIata,
                country, description: city
              } as AutocompletePlace;
              if (!blockSearchByDates(destinationPlace,
                formatDateByRegex(params.get('check_in')))) {
                this.payloadSearchData(params);
              }
            }
          })
        ).subscribe()
    );
  }

  public getDestinationFormat(destination: InputDestinationPlace, params: ParamMap,
    queries: ParamMap, country: string, city: string) {
    let inputType = null;
    if (destination.InputType) {
      inputType = destination.InputType;
    } else {
      inputType = params?.get('destination_type');
    }
    const baseInfo = {
      Id: destination.Id ? destination.Id : Number(params.get('destination_id')),
      InputType: inputType,
      NearestIata: destination?.NearestIata,
      Country: country
    };
    if (inputType === HotelDestinationInputType.hotel) {
      return {
        queries, country, city,
        destinationFormat: {
          ...baseInfo,
          hotel_name: destination.hotel_name ? destination.hotel_name : city,
          address: destination.address ? destination.address : `${city}, ${country}`,
        }
      };
    } else {
      return {
        queries, country, city,
        destinationFormat: {
          ...baseInfo,
          Name: destination.Name ? destination.Name : city,
          NameFull: destination.NameFull ? destination.NameFull : `${city}, ${country}`,
        }
      };
    }
  }

  public destination(params: ParamMap, placeName: string, input: InputDestinationPlace) {
    if (placeName) {
      if (input.InputType === HotelDestinationInputType.hotel) {
        return { params, destination: { ...input, address: placeName } };
      } else {
        return { params, destination: { ...input, NameFull: placeName } };
      }
    } else {
      return { params, destination: input };
    }
  }

  public filterParams(params: ParamMap): boolean {
    return !(isNullOrUndefined(params?.get('destination_type')) || isNullOrUndefined(params?.get('destination_id')) ||
      params?.get('destination_type')?.toLowerCase() === 'null' ||
      params?.get('destination_id')?.toLowerCase() === 'null' || isNaN(Number(params.get('destination_id'))) ||
      !Object.values(HotelDestinationInputType)?.includes(params?.get('destination_type') as HotelDestinationInputType));
  }

  public payloadSearchData(params: ParamMap): void {
    const dateValidation = dateHotelValidation(formatDateByRegex(params.get('check_in')),
      formatDateByRegex(params.get('check_out')));
    if (dateValidation && dateValidation.valid) {
      let dateDeparture = new Date(dateValidation.departure);
      let dateArrival = new Date(dateValidation.arrival);
      if (compareToCurrentDateWithoutTime(dateDeparture)) {
        dateDeparture = null;
      }
      if (compareToCurrentDateWithoutTime(dateArrival)) {
        dateArrival = null;
      }
      this.baseSearchBarFacade.selectCalendarDates([dateDeparture, dateArrival]);
    }
  }

  public setGuestsByParams(): void {
    this.params.Rooms.forEach((roomParams, index) => {
      if (index > 0) {
        this.baseSearchBarFacade.addNewRoom({
          id: index, [TravelerTypes.ADULTS]: 2, [TravelerTypes.CHILDREN]: 0, childrenAges: []
        });
      }
      this.setGuestsPerRoomByParams({
        id: index, [TravelerTypes.ADULTS]: roomParams.Adults,
        [TravelerTypes.CHILDREN]: roomParams.Children, childrenAges: roomParams.ChildrenAges
      });
    });
  }

  public setGuestsPerRoomByParams(event: Room): void {
    this.baseSearchBarFacade.setGuestsPerRoom(event);
  }

  public goBack(): void {
    this.isMobile$.subscribe(isMobile => {
      this.appFacade.historyPop();
      if (!isMobile) {
        this.router.navigate([['/']]);
      } else {
        this.router.navigate([hotelsHomeRootRoute, productHomeRoute]);
      }
    }).unsubscribe();
  }
}
